import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Container, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import { PutJson } from '@awp/helpers/fetchReact';
import { REST_API } from '@awp/constants';
import {
  selectSelectedObservationIds,
  clearAllSelectedObservationIds,
  refreshValidation,
} from '@stores/manualDataValidationSlice';
import { fetchMultiStationData, clearData, selectMultiStationData } from '@stores/dataVisualizationSlice';
import { useMultiStationContext } from '../Graphs/MultiStation/MultiStationContext';

const updateBulkValidation = async (data) => {
  const response = await PutJson(REST_API.OBSERVATIONS_VALIDATION_BULK, data);
  const result = await response.json();

  if (response.ok) {
    if (result) {
      toast.success('Data validation successfully changed!');
    } else {
      toast.info('No data validation changed.');
    }
  } else {
    toast.errors(result);
  }
};

const ValidationForm = ({ children }) => {
  const dispatch = useDispatch();
  const { weatherStations, presetId, weatherStationId, dataRange } = useMultiStationContext();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const selectedObservationIds = useSelector(selectSelectedObservationIds);
  const data = useSelector(selectMultiStationData)?.DataVisualizationDTOs[0];
  const [confLevel, setConfLevel] = useState({});
  const [confLevelStatus, setConfLevelStatus] = useState('');

  const fetchData = async () => {
    dispatch(
      fetchMultiStationData({
        weatherStationIds: [weatherStations[0].Id],
        weatherStationId,
        presetId,
        forceFetch: true,
        endDate: dataRange.endDate,
        displayRange: dataRange.displayRange,
      }),
    );
  };

  useEffect(() => {
    if (!data) {
      setConfLevel({});
      return;
    }

    //transform data for quick access to confidence levels based on observation IDs
    const confLevel = data.Datasets.reduce((result, dataset) => {
      if (dataset.Data) {
        dataset.Data.forEach((dataItem) => {
          result[dataItem.ObservationId] = dataItem.ConfidenceLevel;
        });
      }
      return result;
    }, {});

    setConfLevel(confLevel);
  }, [data]);

  useEffect(() => {
    if (!selectedObservationIds) {
      setConfLevelStatus('');
      return;
    }

    const validExists = selectedObservationIds.some((id) => confLevel[id] === '0');
    const invalidExists = selectedObservationIds.some((id) => confLevel[id] !== '0');
    const status =
      validExists && invalidExists ? 'mixed' : validExists ? 'allValid' : invalidExists ? 'allInvalid' : '';
    setConfLevelStatus(status);
  }, [data, selectedObservationIds]);

  const handleSubmit = async (confidence) => {
    await updateBulkValidation({
      IsValid: confidence,
      ReasonForChange: 'manualValidation',
      Comments: '',
      ObservationIds: selectedObservationIds,
    });

    dispatch(clearAllSelectedObservationIds());
    dispatch(refreshValidation());
    dispatch(clearData());
    await fetchData();
  };

  const isDisabled = selectedObservationIds.length === 0;

  return (
    <>
      <Container>
        <Row>
          {showCancelConfirm ? (
            <Col className="mb-2">
              <AwpYesNoDialog
                action={() => {
                  setShowCancelConfirm(false);
                  dispatch(clearAllSelectedObservationIds());
                  dispatch(refreshValidation());
                }}
                setShowYesNoConfirm={setShowCancelConfirm}
                msg={'Are you sure you want to clear selection?'}
                props={{}}
              />
            </Col>
          ) : (
            <div className="mb-3" style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Button
                type="button"
                className="mr-2"
                variant="primary"
                onClick={() => handleSubmit(true)}
                disabled={isDisabled || confLevelStatus === 'allValid'}
              >
                Valid
              </Button>
              <Button
                type="button"
                className="mr-2"
                variant="danger"
                onClick={() => handleSubmit(false)}
                disabled={isDisabled || confLevelStatus === 'allInvalid'}
              >
                Invalid
              </Button>
              <Button
                className="mr-2"
                variant="outline-primary"
                onClick={() => setShowCancelConfirm(true)}
                disabled={isDisabled}
              >
                Clear Selection
              </Button>
              <span className="ml-2 mt-1">
                {confLevelStatus === 'mixed' && <span>Selected cells contain both valid and invalid data</span>}
                {data && isDisabled && <span>Select observations to enable validation buttons</span>}
              </span>
            </div>
          )}
        </Row>
      </Container>
      {children}
    </>
  );
};

ValidationForm.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default ValidationForm;
